import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id, Paginated } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import {
  IBank,
  IBankConnection,
  IChartOfAccounts,
  IIntegrationsStatus,
  WhatsappConnectionStatus
} from "@/jenesys-types/jenesys-entities/config";

export interface ConnectionsSettingsState {
  platformStatus: IIntegrationsStatus | null;
  platformStatusError?: AppError;
  isFetchingStatus: boolean;
  accountingData: any;
  accountingDataError?: AppError;
  accountingShortData: any;
  accountingShortDataError?: AppError;
  isFetchingBankConnections: boolean;
  bankConnections: Array<IBankConnection>;
  bankConnectionsError?: AppError;
  isRemovingBankConnection: boolean;
  removeBankConnectionError?: AppError;
  isFetchingAcctAuthURL: boolean;
  authorizationUrlError?: AppError;
  isFetchingMailAuthUrl: boolean;
  mailUrlError?: AppError;
  isRefreshing: boolean;
  isSavingDuration: boolean;
  isDisconnectingIntegration: boolean;
  isLinkingSageBusiness: boolean;
  linkingSageBusinessError?: AppError;
  companyAccountingIntegrations: any;
  accountingIntegrationsDataError?: AppError;
  chartOfAccounts: any;
  isLoadingCOAs: boolean;
  chartOfAccountsError?: AppError;
  emailStatus: IIntegrationsStatus;
  emailStatusError?: AppError;
  whatsappStatus: WhatsappConnectionStatus;
  whatsappStatusError?: AppError;
  isSubmittingNumber: boolean;
  submittingNumberError?: AppError;
  banks: Array<IBank>;
  isFetchingBanks: boolean;
  isFetchingYapilyAuthURL: boolean;
  yapilyAuthURLError?: AppError;
}

interface LoadPlatformStatusPayload {
  clientId: Id;
  platform?: string;
  type?: string;
  onSuccess?: (data?: any) => void;
  onError?: (data?: any) => void;
}
export type LoadPlatformStatusAction = PayloadAction<LoadPlatformStatusPayload>;
export type LoadPlatformAuthAction = PayloadAction<LoadPlatformStatusPayload>;
export type LoadMailAuthAction = PayloadAction<LoadPlatformStatusPayload>;
export type LoadPlatformRefreshAction =
  PayloadAction<LoadPlatformStatusPayload>;
export type LoadPlatformDisconnectAction =
  PayloadAction<LoadPlatformStatusPayload>;
export type LoadAccountingIntegrationsDataAction =
  PayloadAction<LoadPlatformStatusPayload>;
export type LoadEmailStatusAction = PayloadAction<LoadPlatformStatusPayload>;
export type LoadWhatsappStatusAction = PayloadAction<LoadPlatformStatusPayload>;

interface LoadPlatformStatusFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type LoadPlatformStatusFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadPlatformAuthFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadMailAuthFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadPlatformRefreshFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadPlatformDisconnectFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadAccountingIntegrationsDataFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadEmailStatusFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;
export type LoadWhatsappStatusFailureAction =
  PayloadAction<LoadPlatformStatusFailurePayload>;

interface LoadPlatformStatusSuccessPayload {
  status: any;
}
interface LoadIntegrationStatusSuccessPayload {
  data: IIntegrationsStatus;
}
export type LoadPlatformStatusSuccessAction =
  PayloadAction<LoadPlatformStatusSuccessPayload>;
export type LoadPlatformAuthSuccessAction =
  PayloadAction<LoadPlatformStatusSuccessPayload>;
export type LoadMailAuthSuccessAction =
  PayloadAction<LoadPlatformStatusSuccessPayload>;
export type LoadPlatformRefreshSuccessAction = PayloadAction<any>;
export type LoadPlatformDisconnectSuccessAction = PayloadAction<any>;
export type LoadAccountingIntegrationsDataSuccessAction = PayloadAction<any>;
export type LoadEmailStatusSuccessAction =
  PayloadAction<LoadIntegrationStatusSuccessPayload>;
export type LoadWhatsappStatusSuccessAction = PayloadAction<{
  data: WhatsappConnectionStatus;
}>;

interface LoadAccountingDataPayload {
  clientId: Id;
}
export type LoadAccountingDataAction = PayloadAction<LoadAccountingDataPayload>;
export type LoadAccountingShortDataAction =
  PayloadAction<LoadAccountingDataPayload>;

interface LoadAccountingDataFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type LoadAccountingDataFailureAction =
  PayloadAction<LoadAccountingDataFailurePayload>;
export type LoadAccountingShortDataFailureAction =
  PayloadAction<LoadAccountingDataFailurePayload>;

interface LoadAccountingDataSuccessPayload {
  data: any;
}
export type LoadAccountingDataSuccessAction =
  PayloadAction<LoadAccountingDataSuccessPayload>;
export type LoadAccountingShortDataSuccessAction =
  PayloadAction<LoadAccountingDataSuccessPayload>;

export type LoadBankConnectionsAction =
  PayloadAction<LoadAccountingDataPayload>;
export type LoadBankConnectionsFailureAction =
  PayloadAction<LoadAccountingDataFailurePayload>;
export type LoadBankConnectionsSuccessAction = PayloadAction<{
  data: Array<IBankConnection>;
}>;

interface LoadLinkBusinessPayload {
  clientId: Id;
  data: {
    business_id: string;
    business_name: string;
    company_id: string;
    platform: string;
  };
  onSuccess?: () => void;
}
export type LoadLinkBusinessAction = PayloadAction<LoadLinkBusinessPayload>;

interface LoadLinkBusinessFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type LoadLinkBusinessFailureAction =
  PayloadAction<LoadLinkBusinessFailurePayload>;

export type LoadLinkBusinessSuccessAction = PayloadAction<any>;

interface LoadChartOfAccountsPayload {
  clientId: Id;
  params: any;
}
export type LoadChartOfAccountsAction =
  PayloadAction<LoadChartOfAccountsPayload>;

interface ChartOfAccountsFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type ChartOfAccountsFailureAction =
  PayloadAction<ChartOfAccountsFailurePayload>;

interface ChartOfAccountsSuccessPayload {
  data: Paginated<IChartOfAccounts>;
}
export type ChartOfAccountsSuccessAction =
  PayloadAction<ChartOfAccountsSuccessPayload>;

interface SubmitWhatsappNumberPayload {
  clientId: Id;
  userId?: Id;
  number: string | null;
  onSuccess: () => void;
  onError: (error: AppError) => void;
}
export type SubmitWhatsappNumberAction =
  PayloadAction<SubmitWhatsappNumberPayload>;

interface SubmitWhatsappNumberFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type SubmitWhatsappNumberFailureAction =
  PayloadAction<SubmitWhatsappNumberFailurePayload>;

export type SubmitWhatsappNumberSuccessAction = PayloadAction<any>;

interface SpecifyConnectionDurationPayload {
  fromDate: string;
  toDate: string;
  companyId: Id;
  connectionId: Id;
  onSuccess: (response: any) => void;
}
export type SpecifyConnectionDurationAction =
  PayloadAction<SpecifyConnectionDurationPayload>;
export type SpecifyConnectionDurationFailureAction =
  PayloadAction<LoadAccountingDataFailurePayload>;
export type SpecifyConnectionDurationSuccessAction = PayloadAction<any>;

export type FetchBanksAction = PayloadAction<{ query: string }>;
export type FetchBanksFailureAction = PayloadAction<{ error?: AppError }>;
export type FetchBanksSuccessAction = PayloadAction<{ data: Array<IBank> }>;

export type AuthorizeYapilyAction = PayloadAction<{
  companyId: Id;
  institutionId: string;
  onSuccess: (url: string) => void;
}>;
export type AuthorizeYapilyFailureAction = PayloadAction<{ error?: AppError }>;
export type AuthorizeYapilySuccessAction = PayloadAction<{
  authorisation_url: string;
}>;

interface UpdateWhatsappNumberPayload {
  connectionId: string;
  number: string;
  onSuccess: () => void;
  onError: (error: AppError) => void;
}
export type UpdateWhatsappNumberAction =
  PayloadAction<UpdateWhatsappNumberPayload>;

interface UpdateWhatsappNumberFailurePayload {
  error?: AppError;
  connectionId?: string;
}
export type UpdateWhatsappNumberFailureAction =
  PayloadAction<UpdateWhatsappNumberFailurePayload>;

export type UpdateWhatsappNumberSuccessAction = PayloadAction<any>;

interface RemoveBankConnectionPayload {
  clientId: Id;
  connectionId: Id;
  onSuccess?: () => void;
}

const name = "state/features/ClientSettings/Connections";

export const initialState: ConnectionsSettingsState = {
  platformStatus: null,
  platformStatusError: undefined,
  isFetchingStatus: false,
  accountingData: {},
  accountingDataError: undefined,
  accountingShortData: {},
  accountingShortDataError: undefined,
  isFetchingBankConnections: true,
  bankConnections: [],
  bankConnectionsError: undefined,
  isRemovingBankConnection: false,
  removeBankConnectionError: undefined,
  isFetchingAcctAuthURL: false,
  authorizationUrlError: undefined,
  isFetchingMailAuthUrl: false,
  mailUrlError: undefined,
  isRefreshing: false,
  isSavingDuration: false,
  isDisconnectingIntegration: false,
  isLinkingSageBusiness: false,
  companyAccountingIntegrations: [],
  accountingIntegrationsDataError: undefined,
  chartOfAccounts: {},
  isLoadingCOAs: false,
  chartOfAccountsError: undefined,
  emailStatus: {},
  emailStatusError: undefined,
  whatsappStatus: {},
  whatsappStatusError: undefined,
  isSubmittingNumber: false,
  submittingNumberError: undefined,
  banks: [],
  isFetchingBanks: false,
  isFetchingYapilyAuthURL: false,
  yapilyAuthURLError: undefined
};

const ConnectionsSettingsSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadPlatformStatus: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadPlatformStatusAction
      ) => {
        state.platformStatus = null;
        state.isFetchingStatus = true;
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    loadPlatformStatusFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadPlatformStatusFailureAction
      ) => {
        state.platformStatusError = action.payload.error;
        state.isFetchingStatus = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadPlatformStatusSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadPlatformStatusSuccessAction
      ) => {
        state.platformStatus = action.payload.status;
        state.isFetchingStatus = false;
      },
      prepare: (status: any) => ({
        payload: { status }
      })
    },
    loadAccountingData: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadAccountingDataAction
      ) => {
        state.accountingData = {};
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    loadAccountingDataFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingDataFailureAction
      ) => {
        state.accountingDataError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadAccountingDataSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingDataSuccessAction
      ) => {
        if (
          action.payload.data?.completed_steps?.includes("store_accounts") &&
          action.payload.data?.completed_steps?.includes("store_tax_codes") &&
          action.payload.data?.completed_steps?.includes("store_vendors") &&
          action.payload.data?.completed_steps?.includes("store_currencies")
        ) {
          action.payload.data.status = "completed";
        }
        state.accountingData = action.payload.data;
      },
      prepare: (data: any) => ({
        payload: { data }
      })
    },

    loadBankConnections: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadBankConnectionsAction
      ) => {
        state.isFetchingBankConnections = true;
        state.bankConnections = [];
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    loadBankConnectionsFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadBankConnectionsFailureAction
      ) => {
        state.bankConnectionsError = action.payload.error;
        state.isFetchingBankConnections = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadBankConnectionsSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadBankConnectionsSuccessAction
      ) => {
        state.bankConnections = action.payload.data;
        state.isFetchingBankConnections = false;
      },
      prepare: (data: any) => ({
        payload: { data }
      })
    },
    removeBankConnection: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: PayloadAction<RemoveBankConnectionPayload>
      ) => {
        state.isRemovingBankConnection = true;
        state.removeBankConnectionError = undefined;
      },
      prepare: (payload: RemoveBankConnectionPayload) => ({ payload })
    },
    removeBankConnectionFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: PayloadAction<AppError>
      ) => {
        state.isRemovingBankConnection = false;
        state.removeBankConnectionError = action.payload;
      },
      prepare: (error?: AppError) => ({ payload: error! })
    },
    removeBankConnectionSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isRemovingBankConnection = false;
      },
      prepare: () => ({ payload: undefined })
    },
    loadAccountingShortData: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadAccountingShortDataAction
      ) => {
        state.accountingShortData = {};
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    loadAccountingShortDataFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingShortDataFailureAction
      ) => {
        state.accountingShortDataError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadAccountingShortDataSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingShortDataSuccessAction
      ) => {
        state.accountingShortData = action.payload.data;
      },
      prepare: (data: any) => ({
        payload: { data }
      })
    },
    loadIntegrationAuthorize: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadPlatformAuthAction
      ) => {
        state.isFetchingAcctAuthURL = true;
      },
      prepare: (
        clientId: Id,
        platform: string,
        onSuccess: (data: any) => void
      ) => ({
        payload: { clientId, platform, onSuccess }
      })
    },
    loadIntegrationAuthorizeFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadPlatformAuthFailureAction
      ) => {
        state.isFetchingAcctAuthURL = false;
        state.authorizationUrlError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadIntegrationAuthorizeSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingAcctAuthURL = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadMailAuthorize: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadMailAuthAction
      ) => {
        state.isFetchingMailAuthUrl = true;
      },
      prepare: (
        clientId: Id,
        platform: string,
        onSuccess: (data: any) => void
      ) => ({
        payload: { clientId, platform, onSuccess }
      })
    },
    loadMailAuthorizeFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadMailAuthFailureAction
      ) => {
        state.isFetchingMailAuthUrl = false;
        state.mailUrlError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadMailAuthorizeSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingMailAuthUrl = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadIntegrationRefresh: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadPlatformRefreshAction
      ) => {
        state.isRefreshing = true;
      },
      prepare: (
        clientId: Id,
        platform: string,
        onSuccess: () => void,
        onError: () => void
      ) => ({
        payload: { clientId, platform, onSuccess, onError }
      })
    },
    loadIntegrationRefreshFailure: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isRefreshing = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadIntegrationRefreshSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingAcctAuthURL = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadIntegrationDisconnect: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadPlatformDisconnectAction
      ) => {
        state.isDisconnectingIntegration = true;
      },
      prepare: (clientId: Id, platform: string, type: string) => ({
        payload: { clientId, platform, type }
      })
    },
    loadIntegrationDisconnectFailure: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isDisconnectingIntegration = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadIntegrationDisconnectSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isDisconnectingIntegration = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadLinkBusiness: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadLinkBusinessAction
      ) => {
        state.isDisconnectingIntegration = true;
      },
      prepare: (clientId: Id, data: any, onSuccess: () => void) => ({
        payload: { clientId, data, onSuccess }
      })
    },
    loadLinkBusinessFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadLinkBusinessFailureAction
      ) => {
        state.isDisconnectingIntegration = false;
        state.linkingSageBusinessError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadLinkBusinessSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isDisconnectingIntegration = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadAccountingIntegration: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadAccountingIntegrationsDataAction
      ) => {
        state.companyAccountingIntegrations = undefined;
      },
      prepare: (clientId: Id, platform: string) => ({
        payload: { clientId, platform }
      })
    },
    loadAccountingIntegrationFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingIntegrationsDataFailureAction
      ) => {
        state.accountingDataError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadAccountingIntegrationSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadAccountingIntegrationsDataSuccessAction
      ) => {
        state.companyAccountingIntegrations = action.payload.data;
      },
      prepare: (data: any) => ({
        payload: { data }
      })
    },
    loadChartOfAccounts: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadChartOfAccountsAction
      ) => {
        if (_action.payload.params.searchQuery === "")
          state.chartOfAccounts = { items: [] };
        state.isLoadingCOAs = true;
      },
      prepare: (clientId: Id, params: any) => ({
        payload: { clientId, params }
      })
    },
    loadChartOfAccountsFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: ChartOfAccountsFailureAction
      ) => {
        state.chartOfAccountsError = action.payload.error;
        state.isLoadingCOAs = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadChartOfAccountsSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: ChartOfAccountsSuccessAction
      ) => {
        state.isLoadingCOAs = false;
        state.chartOfAccounts = action.payload.data;
      },
      prepare: (data: Paginated<IChartOfAccounts>) => ({
        payload: { data }
      })
    },
    loadEmailStatus: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: LoadEmailStatusAction
      ) => {
        state.emailStatus = {};
      },
      prepare: (clientId: Id, platform: string) => ({
        payload: { clientId, platform }
      })
    },
    loadEmailStatusFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadEmailStatusFailureAction
      ) => {
        state.emailStatusError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadEmailStatusSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadEmailStatusSuccessAction
      ) => {
        state.emailStatus = action.payload.data;
      },
      prepare: (data: IIntegrationsStatus) => ({
        payload: { data }
      })
    },
    loadWhatsappStatus: {
      reducer: (state: ConnectionsSettingsState) => {
        state.whatsappStatus = {};
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadWhatsappStatusFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadWhatsappStatusFailureAction
      ) => {
        state.whatsappStatusError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadWhatsappStatusSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: LoadWhatsappStatusSuccessAction
      ) => {
        state.whatsappStatus = action.payload.data;
      },
      prepare: (data: WhatsappConnectionStatus) => ({
        payload: { data }
      })
    },
    submitWpNumber: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: SubmitWhatsappNumberAction
      ) => {
        state.isSubmittingNumber = true;
      },
      prepare: (payload: SubmitWhatsappNumberPayload) => ({
        payload
      })
    },
    submitWpNumberFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: SubmitWhatsappNumberFailureAction
      ) => {
        state.isSubmittingNumber = false;
        state.submittingNumberError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    submitWpNumberSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isSubmittingNumber = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    specifyConnectionDuration: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: SpecifyConnectionDurationAction
      ) => {
        state.isSavingDuration = true;
      },
      prepare: (payload: SpecifyConnectionDurationPayload) => ({
        payload
      })
    },
    specifyConnectionDurationFailure: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isSavingDuration = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    specifyConnectionDurationSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isSavingDuration = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    fetchBanks: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingBanks = true;
      },
      prepare: (query?: string) => ({
        payload: { query }
      })
    },
    fetchBanksFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: FetchBanksFailureAction
      ) => {
        state.isFetchingBanks = false;
        state.bankConnectionsError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    fetchBanksSuccess: {
      reducer: (
        state: ConnectionsSettingsState,
        action: FetchBanksSuccessAction
      ) => {
        state.isFetchingBanks = false;
        state.banks = action.payload.data;
      },
      prepare: (data: Array<IBank>) => ({
        payload: { data }
      })
    },
    authorizeYapily: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingYapilyAuthURL = true;
      },
      prepare: (payload: AuthorizeYapilyAction["payload"]) => ({
        payload
      })
    },
    authorizeYapilyFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: AuthorizeYapilyFailureAction
      ) => {
        state.isFetchingYapilyAuthURL = false;
        state.yapilyAuthURLError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    authorizeYapilySuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isFetchingYapilyAuthURL = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    updateWhatsappNumber: {
      reducer: (
        state: ConnectionsSettingsState,
        _action: UpdateWhatsappNumberAction
      ) => {
        state.isSubmittingNumber = true;
      },
      prepare: (payload: UpdateWhatsappNumberPayload) => ({
        payload
      })
    },
    updateWhatsappNumberFailure: {
      reducer: (
        state: ConnectionsSettingsState,
        action: UpdateWhatsappNumberFailureAction
      ) => {
        state.isSubmittingNumber = false;
        state.submittingNumberError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    updateWhatsappNumberSuccess: {
      reducer: (state: ConnectionsSettingsState) => {
        state.isSubmittingNumber = false;
      },
      prepare: () => ({
        payload: {}
      })
    }
  }
});

export const {
  loadPlatformStatus,
  loadPlatformStatusFailure,
  loadPlatformStatusSuccess,
  loadAccountingData,
  loadAccountingDataFailure,
  loadAccountingDataSuccess,
  loadBankConnections,
  loadBankConnectionsFailure,
  loadBankConnectionsSuccess,
  removeBankConnection,
  removeBankConnectionFailure,
  removeBankConnectionSuccess,
  loadAccountingShortData,
  loadAccountingShortDataFailure,
  loadAccountingShortDataSuccess,
  loadIntegrationAuthorize,
  loadIntegrationAuthorizeFailure,
  loadIntegrationAuthorizeSuccess,
  loadMailAuthorize,
  loadMailAuthorizeFailure,
  loadMailAuthorizeSuccess,
  loadIntegrationRefresh,
  loadIntegrationRefreshFailure,
  loadIntegrationRefreshSuccess,
  loadIntegrationDisconnect,
  loadIntegrationDisconnectFailure,
  loadIntegrationDisconnectSuccess,
  loadLinkBusiness,
  loadLinkBusinessFailure,
  loadLinkBusinessSuccess,
  loadAccountingIntegration,
  loadAccountingIntegrationFailure,
  loadAccountingIntegrationSuccess,
  loadChartOfAccounts,
  loadChartOfAccountsFailure,
  loadChartOfAccountsSuccess,
  loadEmailStatus,
  loadEmailStatusFailure,
  loadEmailStatusSuccess,
  loadWhatsappStatus,
  loadWhatsappStatusFailure,
  loadWhatsappStatusSuccess,
  submitWpNumber,
  submitWpNumberFailure,
  submitWpNumberSuccess,
  specifyConnectionDuration,
  specifyConnectionDurationFailure,
  specifyConnectionDurationSuccess,
  fetchBanks,
  fetchBanksFailure,
  fetchBanksSuccess,
  authorizeYapily,
  authorizeYapilyFailure,
  authorizeYapilySuccess,
  updateWhatsappNumber,
  updateWhatsappNumberFailure,
  updateWhatsappNumberSuccess
} = ConnectionsSettingsSlice.actions;

// Selectors
const selectConnectionsSettingsState = (state: {
  features: { ConnectionsSettings: ConnectionsSettingsState };
}): ConnectionsSettingsState => state.features.ConnectionsSettings;

export const selectConnectionsSettings = createSelector(
  selectConnectionsSettingsState,
  (state) => state
);

export const selectAccountingShortData = createSelector(
  selectConnectionsSettingsState,
  (state) => state.accountingData
);

export const selectPlatformStatusByNameData = createSelector(
  [selectConnectionsSettingsState],
  (state) => state.platformStatus
);

export const selectIsAccountingPlatformConnected = createSelector(
  selectConnectionsSettingsState,
  (state) =>
    state.platformStatus?.user_status === "valid" &&
    state.platformStatus?.company_status === "valid"
);

export const selectIsCompanyConnectedToPlatform = createSelector(
  selectConnectionsSettingsState,
  (state) => state.platformStatus?.company_status === "valid"
);

export default ConnectionsSettingsSlice.reducer;
